<template>
  <v-dialog
    persistent
    max-width="380"
    v-model="show"
    @keydown.esc="handleCancel"
  >
    <v-card class="pl-1 pr-1 py-4 d-flex flex-column justify-center">
      <DateSelectionStep
        v-if="currentStep === STEPS.DATE_SELECTION"
        :singleDateValue="recurrenceSettings.recurrenceStartDate"
        :selectedHour="recurrenceSettings.reminderHour"
        @setRepetition="handleSetRepetition"
        @onDateChange="handleDateChange"
        @onHourChange="recurrenceSettings.reminderHour = $event"
      />

      <div v-if="currentStep === STEPS.SET_REMINDER">
        <FrequencySelectionStep
          :settings="recurrenceSettings"
          @change="handleSettingsChange"
          @setOneTime="handleSetOneTime"
        />

        <div
          v-if="hasInvalidHourlyRange"
          class="caption red--text mt-4 px-4"
          data-testid="routineReminderModal-text-hourlyEndAfterStart"
        >
          <v-icon small color="red" class="mr-1"
            >mdi-alert-circle-outline</v-icon
          >
          {{ $t('routines_reminder.hourly_end_after_start') }}
        </div>
      </div>

      <v-divider class="my-4 mx-4" />

      <div class="px-4 mb-4">
        <span
          class="subtitle2 font-weight-medium blue-grey--text text--darken-3"
        >
          {{ `${$t('routines_reminder.notifications')}*` }}
        </span>
        <div v-if="isEmptySelectedNotifications" class="caption red--text mt-2">
          <v-icon small color="red" class="mr-1"
            >mdi-alert-circle-outline</v-icon
          >
          {{ $t('routines_reminder.please_select') }}
        </div>

        <div class="d-flex py-0 mt-4">
          <v-checkbox
            v-for="option in visibleNotificationOptions"
            multiple
            hide-details
            v-model="notification"
            class="ma-0 pa-0 mr-14"
            :data-testid="
              `routineReminderModal-checkbox-notification-${option.key}`
            "
            :disabled="isHourlyReminder"
            :key="option.key"
            :value="option.key"
            @change="handleSettingsChange('notificationType', notification)"
          >
            <template v-slot:label>
              <span class="body2 blue-grey--text text--darken-2">{{
                option.label
              }}</span>
            </template>
          </v-checkbox>
        </div>

        <div
          v-if="isHourlyReminder"
          class="caption blue-grey--text text--darken-1 mt-2"
          data-testid="routineReminderModal-text-hourlyPushOnlyCaption"
        >
          {{ $t('routines_reminder.hourly_push_only_caption') }}
        </div>
      </div>

      <DeleteConfirmationModal
        :show="showDeleteConfirmationModal"
        :title="$t('routines_reminder.confirm_deletion')"
        :text="$t('routines_reminder.are_you_sure_you_want_to_delete')"
        :isLoading="isLoadingReminder"
        @cancel="handleCancelDelete"
        @delete="handleDeleteReminder"
      />

      <v-card-actions class="ma-0 px-4 pb-0 justify-end">
        <Button
          v-if="isEditMode"
          plain
          color="danger"
          class="mr-auto"
          data-testid="routineReminderModal-button-deleteReminder"
          :loading="isLoadingReminder"
          :text="$t('common.button.delete')"
          @click="showDeleteConfirmationModal = true"
        />
        <Button
          plain
          color="grey"
          class="mr-4"
          :text="$t('common.button.cancel')"
          @click="handleCancel"
        />
        <Button
          plain
          color="primary"
          :disabled="hasInvalidHourlyRange"
          :loading="isLoadingReminder"
          :text="$t('common.button.save')"
          @click="handleSaveReminder"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import { FREQUENCY_TYPE, NOTIFICATION_TYPE } from '@/lib/frequencies'

import DateSelectionStep from './DateSelectionStep.vue'
import Button from '@/components/UI/Button.vue'
import FrequencySelectionStep from './FrequencySelectionStep.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

const STEPS = {
  DATE_SELECTION: 'DATE_SELECTION',
  SET_REMINDER: 'SET_REMINDER',
}

const DEFAULT_RECURRENCE_SETTINGS = {
  frequency: FREQUENCY_TYPE.DAILY,
  interval: 1,
  until: moment().format('YYYY-MM-DD'),
  untilHour: '8:0',
  byWeekDay: [],
  reminderHour: '8:0',
  recurrenceStartDate: moment().format('YYYY-MM-DD'),
  notificationType: [NOTIFICATION_TYPE.EMAIL, NOTIFICATION_TYPE.PUSH],
  bySetPosition: [],
  byMonthDay: [],
}

export default {
  name: 'RoutineReminderModal',
  mixins: [mixin, trackerMixin],

  data() {
    return {
      NOTIFICATION_TYPE,
      FREQUENCY_TYPE,
      STEPS,
      recurrenceSettings: {},
      notification: [],
      currentStep: this.isRepeatedReminder
        ? STEPS.SET_REMINDER
        : STEPS.DATE_SELECTION,
      isLoadingReminder: false,
      showDeleteConfirmationModal: false,
      notificationOption: [
        {
          key: NOTIFICATION_TYPE.EMAIL,
          label: this.$t('routines_reminder.email'),
        },
        {
          key: NOTIFICATION_TYPE.PUSH,
          label: this.$t('routines_reminder.push'),
        },
      ],
    }
  },

  async created() {
    this.recurrenceSettings = this.isEditMode
      ? this.formattedRecurrence
      : _.cloneDeep(DEFAULT_RECURRENCE_SETTINGS)
    this.notification = this.isEditMode
      ? this.reminder[0]?.notifications
      : [NOTIFICATION_TYPE.EMAIL, NOTIFICATION_TYPE.PUSH]
  },

  components: {
    DeleteConfirmationModal,
    DateSelectionStep,
    FrequencySelectionStep,
    Button,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    isEditMode: Boolean,
    isRepeatedReminder: Boolean,
    recurrence: {
      type: Object,
      required: true,
    },
    reminder: {
      type: Array,
      required: true,
    },
    routineDefinitionId: String,
    reminderId: String,
  },

  watch: {
    isEditMode: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.recurrenceSettings = _.cloneDeep(DEFAULT_RECURRENCE_SETTINGS)
        }
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    localStartDate() {
      return moment.utc(this.recurrence.startDate).local()
    },

    localUntilDate() {
      return moment.utc(this.recurrence?.until).local()
    },

    isHourlyReminder() {
      return this.recurrenceSettings.frequency === FREQUENCY_TYPE.HOURLY
    },

    isEmptySelectedNotifications() {
      return _.isEmpty(this.notification)
    },

    visibleNotificationOptions() {
      if (!this.isHourlyReminder) return this.notificationOption

      return [
        {
          key: NOTIFICATION_TYPE.PUSH,
          label: this.$t('routines_reminder.push'),
        },
      ]
    },

    formattedRecurrence() {
      return {
        ...this.recurrence,
        bySetPosition:
          _.isEmpty(this.recurrence.bySetPosition) ||
          !this.recurrence.bySetPosition
            ? []
            : this.recurrence.bySetPosition,
        recurrenceStartDate: this.localStartDate?.format('YYYY-MM-DD'),
        reminderHour: this.localStartDate?.format('H:m'),
        until: this.recurrence?.until
          ? this.localUntilDate?.format('YYYY-MM-DD')
          : null,
        untilHour: this.recurrence?.until
          ? this.localUntilDate?.format('H:m')
          : null,
        notificationType: this.reminder[0]?.notifications,
        byWeekDay: this.recurrence.byWeekday,
      }
    },

    rruleStartDate() {
      const startDateObject = moment(
        this.recurrenceSettings?.recurrenceStartDate
      )
      const hoursAndMinutes = this.recurrenceSettings?.reminderHour
        ?.split(':')
        ?.map(Number) ?? [0, 0]

      const fullStartDateObject = new Date(startDateObject).setHours(
        hoursAndMinutes?.[0],
        hoursAndMinutes?.[1],
        0
      )
      return moment.utc(fullStartDateObject)
    },

    rruleUntilDate() {
      const endDateObject = moment(this.recurrenceSettings?.until)
      const hourSource = this.isHourlyReminder
        ? this.recurrenceSettings?.untilHour
        : this.recurrenceSettings?.reminderHour
      const hoursAndMinutes = hourSource?.split(':')?.map(Number) ?? [0, 0]

      const fullUntilDateObject = new Date(endDateObject).setHours(
        hoursAndMinutes?.[0],
        hoursAndMinutes?.[1],
        0
      )
      return moment.utc(fullUntilDateObject)
    },

    hasInvalidHourlyRange() {
      if (!this.isHourlyReminder || !this.recurrenceSettings?.until)
        return false

      return !this.rruleUntilDate.isAfter(this.rruleStartDate)
    },
  },

  methods: {
    handleDateChange(date) {
      this.recurrenceSettings.recurrenceStartDate = date
      this.recurrenceSettings.until = date
    },

    handleSettingsChange(property, value) {
      if (property === 'setLastDay') {
        return (this.recurrenceSettings = {
          ...this.recurrenceSettings,
          ...value,
        })
      }

      if (property === 'frequency' && value === FREQUENCY_TYPE.HOURLY) {
        this.notification = [NOTIFICATION_TYPE.PUSH]
        this.recurrenceSettings.notificationType = [NOTIFICATION_TYPE.PUSH]
      }

      if (property === 'frequency' && value !== FREQUENCY_TYPE.HOURLY) {
        this.notification = [NOTIFICATION_TYPE.EMAIL, NOTIFICATION_TYPE.PUSH]
        this.recurrenceSettings.notificationType = [
          NOTIFICATION_TYPE.EMAIL,
          NOTIFICATION_TYPE.PUSH,
        ]
      }

      this.recurrenceSettings[property] = value
    },

    handleCancel() {
      this.showDeleteConfirmationModal = false
      this.$emit('close')
      this.trackRoutineDefinitionReminder('reminder.cancelSet', {
        isEdit: this.isEditMode,
        entityType: 'routineDefinition',
        reminderId: this.reminderId,
      })
      this.recurrenceSettings = _.cloneDeep(DEFAULT_RECURRENCE_SETTINGS)
      this.currentStep = STEPS.DATE_SELECTION
    },

    handleCancelDelete() {
      this.showDeleteConfirmationModal = false
      this.trackRoutineDefinitionReminder('reminder.delete', {
        process: 'cancel',
        entityType: 'routineDefinition',
        reminderId: this.reminderId,
      })
    },

    handleDeleteReminder() {
      this.showDeleteConfirmationModal = false
      this.$emit('deleteReminder', this.reminderId)
    },

    handleSetRepetition() {
      if (!this.isEditMode) this.recurrenceSettings.until = null
      this.currentStep = STEPS.SET_REMINDER
      this.trackRoutineDefinitionReminder('reminder.setRepetition', {
        entityType: 'routineDefinition',
        reminderId: this.reminderId,
      })
    },

    async handleSaveReminder() {
      if (this.hasInvalidHourlyRange) return

      try {
        this.isLoadingReminder = true
        const payload = {
          recurrence: {
            startDate: this.rruleStartDate,
            frequency: this.recurrenceSettings.frequency,
            interval: this.recurrenceSettings.interval,
            until: this.rruleUntilDate,
            bySetPosition: this.recurrenceSettings?.bySetPosition ?? [],
            byMonthDay: this.recurrenceSettings.byMonthDay,
            byWeekDay: this.recurrenceSettings.byWeekDay,
            timezone: this.account.timezone,
          },
          notifications: this.recurrenceSettings.notificationType,
          entityType: 'routineDefinition',
          entityId: this.routineDefinitionId,
        }
        this.isEditMode
          ? await api.reminder.updateReminder(
              this.organizationId,
              this.reminderId,
              payload
            )
          : await api.reminder.createReminder(this.organizationId, payload)
        this.$emit('saveReminder')
        this.isEditMode
          ? this.showSuccess(this.$t('routines_reminder.updated_successfully'))
          : this.showSuccess(this.$t('routines_reminder.created_successfully'))

        this.trackRoutineDefinitionReminder('reminder.save', {
          isEdit: this.isEditMode,
          period: this.recurrenceSettings.frequency,
          interval: this.recurrenceSettings.interval,
          isOneTime: !this.isRepeatedReminder,
          weekDays: this.recurrenceSettings.byWeekDay,
          startDate: moment
            .utc(payload.recurrence.startDate)
            .local()
            .format(),
          hour: this.recurrenceSettings?.reminderHour,
          monthDays:
            this.recurrenceSettings.byMonthDay.length > 1
              ? 'last_day'
              : this.recurrenceSettings.byMonthDay[0],
          until: moment
            .utc(payload.recurrence.until)
            .local()
            .format(),
          notifications: this.recurrenceSettings.notificationType,
          reminderId: this.reminderId,
          entityType: 'routineDefinition',
        })
        this.handleCancel()
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isLoadingReminder = false
      }
    },

    handleSetOneTime() {
      this.recurrenceSettings.until = this.rruleStartDate.format()
      this.recurrenceSettings.bySetPosition = []
      this.recurrenceSettings.byMonthDay = []
      this.recurrenceSettings.byWeekDay = []
      this.recurrenceSettings.interval = 1
      this.recurrenceSettings.frequency = FREQUENCY_TYPE.DAILY
      this.currentStep = STEPS.DATE_SELECTION
      this.trackRoutineDefinitionReminder('reminder.setOneTime', {
        reminderId: this.reminderId,
        entityType: 'routineDefinition',
      })
    },
  },
}
</script>
